import type { FC } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { Theme } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { useMutation, useQueries } from "@tanstack/react-query";
import { partnerByIdQueryOptions } from "../../api/partners";
import { useGetSystemById } from "../../api/systems";
import type { GridViewAgreement } from "../../api/systems.types";
import { DynamicGrid } from "../../dynamicGrid";
import { XIcon } from "../../shared/icons";
import { usePermission } from "../../shared/permissions";
import { AgreementLink } from "../../agreements/AgreementLink";
import { DownloadFileMessage } from "../../dynamicShared/DocumentLink";
import { downloadFileMutationOptions } from "../../api/files";
import { EXTERNAL_LINK_ICON_GAP, EXTERNAL_LINK_ICON_SIZE } from "../../dynamicShared/Link";
import { getPartnerIds, SUPPLIER_QUESTION_ID } from "../../agreements/supplierQuestions";
import type { InputProps } from "../DynamicInput";
import type { MultipleAgreementValue, MultiplePartnerValue, SelectedAgreement } from "../types";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import { useDynamicFormState } from "../DynamicFormProvider";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";
import { AddDialogTrigger } from "./AddDialogTrigger";
import { AddAgreementDialog } from "./AddAgreementDialog";

export const MultipleAgreementInput: FC<InputProps<MultipleAgreementValue>> = ({ questionCard, onSave, readonly }) => {
  const { t } = useTranslation();
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();
  const { hasPermission } = usePermission();
  const canReadPartners = hasPermission("nova-forms-api:partner:read");
  const { control } = useFormContext();
  const { field } = useController({ control, name: questionCard.id });
  const selectedAgreements = (field.value ?? []) as MultipleAgreementValue;
  const suppliers = (useWatch({ control, name: SUPPLIER_QUESTION_ID }) ?? []) as MultiplePartnerValue;
  const supplierPartnerIds = getPartnerIds(suppliers);
  const partnerQueries = useQueries({
    queries: supplierPartnerIds.map(partnerId => ({
      ...partnerByIdQueryOptions(partnerId),
      throwOnError: false,
      enabled: canReadPartners,
    })),
  });
  const partners = partnerQueries.flatMap(query => (query.data ? [query.data] : []));
  const isPartnersLoading = partnerQueries.some(query => query.isLoading);
  const isPartnersError = partnerQueries.some(query => query.isError);
  const systemQuery = useGetSystemById(DynamicGrid.useSelectedRowId());
  const systemAgreements = systemQuery.data?.agreements ?? [];
  const saveMutation = useMutation({ mutationFn: (value: MultipleAgreementValue) => onSave(value) });
  const isReadOnly = readonly || !canReadPartners;

  const showPartnerName = supplierPartnerIds.length > 1;
  const visibleAgreements = selectedAgreements.flatMap(selected => {
    const agreement = systemAgreements.find(candidate => candidate.id === selected.agreementId);
    return agreement ? [{ selected, agreement }] : [];
  });
  const isAgreementsUnresolved =
    systemQuery.isSuccess && selectedAgreements.length > 0 && visibleAgreements.length === 0;

  const addAgreement = (agreementToAdd: SelectedAgreement) => {
    const newValue = [...selectedAgreements, agreementToAdd];
    return saveMutation.mutateAsync(newValue, { onSuccess: () => field.onChange(newValue) });
  };

  const removeAgreement = (agreementToRemove: SelectedAgreement) => {
    const newValue = selectedAgreements.filter(selected => selected.agreementId !== agreementToRemove.agreementId);
    field.onChange(newValue);
    saveMutation.mutate(newValue);
  };

  return (
    <Stack sx={{ mb: 0.5 }}>
      {isAgreementsUnresolved ? (
        <ErrorMessage />
      ) : (
        visibleAgreements.map(({ selected, agreement }) => (
          <Item
            key={selected.agreementId}
            agreement={agreement}
            partnerName={showPartnerName ? agreement.partnerName : undefined}
            readonly={isReadOnly}
            onRemoveClick={() => removeAgreement(selected)}
          />
        ))
      )}

      <Box>
        <AddDialogTrigger
          renderDialog={({ onClose }) => (
            <AddAgreementDialog
              onClose={onClose}
              onSubmit={addAgreement}
              partners={partners}
              isPartnersLoading={isPartnersLoading}
              isPartnersError={isPartnersError}
              selectedAgreements={selectedAgreements}
              canCreateAgreement={hasPermission("nova-forms-api:partner:update")}
            />
          )}
          isEmpty={selectedAgreements.length === 0}
          isReadOnly={isReadOnly}
        >
          {t("Partner.Agreement.AddButton")}
        </AddDialogTrigger>
        {saveMutation.isError && <ErrorMessage />}
        {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
          <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
        )}
      </Box>
    </Stack>
  );
};

type ItemProps = {
  agreement: GridViewAgreement;
  partnerName: string | undefined;
  readonly: boolean;
  onRemoveClick: () => void;
};

const ACTION_BUTTONS_CLASS_NAME = "action-buttons";
const INDENT_PAST_LINK_ICON = (theme: Theme) =>
  `calc(${EXTERNAL_LINK_ICON_SIZE}px + ${theme.spacing(EXTERNAL_LINK_ICON_GAP)})`;

const Item: FC<ItemProps> = ({ agreement, partnerName, readonly, onRemoveClick }) => {
  const { t } = useTranslation();
  const downloadFileMutation = useMutation(downloadFileMutationOptions());
  return (
    <Box>
      <Stack
        sx={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          py: 0.2,
          px: 2,
          ...(!readonly && {
            "&:hover": { backgroundColor: theme => theme.palette.grey["100"] },
            [`&:hover .${ACTION_BUTTONS_CLASS_NAME}, &:focus-within .${ACTION_BUTTONS_CLASS_NAME}`]: {
              visibility: "visible",
            },
          }),
        }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <AgreementLink
            agreement={agreement}
            downloadFileMutation={downloadFileMutation}
            sx={{ lineHeight: "inherit" }}
          />
          {partnerName && (
            <Typography
              sx={{
                pl: INDENT_PAST_LINK_ICON,
                fontSize: theme => theme.typography.pxToRem(12),
                color: theme => theme.palette.text.secondary,
              }}
            >
              {partnerName}
            </Typography>
          )}
        </Stack>
        {!readonly && (
          <Stack direction="row" className={ACTION_BUTTONS_CLASS_NAME} sx={{ visibility: "hidden" }}>
            <Tooltip title={t("DynamicForm.MultipleAgreementInput.Remove")}>
              <IconButton aria-label={t("DynamicForm.MultipleAgreementInput.Remove")} onClick={onRemoveClick}>
                <XIcon size={14} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <DownloadFileMessage downloadFileMutation={downloadFileMutation} />
    </Box>
  );
};
